import React, { useCallback } from 'react';
import { Row, Col, Button, Form } from 'react-bootstrap';
import { UseFormRegister, UseFormSetValue, UseFormWatch, FieldErrors, UseFormTrigger } from 'react-hook-form';
import { CustomDropdown, WeekOffDaysDropdown, PriceInputField, DatePickerField } from './common';
import { Tooltip } from './common/tooltip.component';
import ServicesTable from './services-table.component';
import { AllowancesDeductionsTable } from './AllowancesDeductionsTable';
import PensionHolidaySection from './pension-holiday-section.component';

interface DefaultInvoiceSectionProps {
  // Form props
  register: UseFormRegister<any>;
  setValue: UseFormSetValue<any>;
  watch: UseFormWatch<any>;
  errors: FieldErrors;
  trigger: UseFormTrigger<any>;
  
  // Configuration values
  isRegularEngagement: boolean;
  currency: string;
  isHourlyPrice: boolean;
  isDailyPrice: boolean;
  isWeeklyPrice: boolean;
  isMonthlyPrice: boolean;
  deductionType: string;
  hoursPerDayType: string;
  workDates: string;
  showDates: string;
  selectedCustomer?: any;
  
  // Dropdown states
  isHoursPerDayDropdownOpen: boolean;
  isShowDatesDropdownOpen: boolean;
  isWeekOffDaysDropdownOpen: boolean;
  showDatesDropdownRef: React.RefObject<HTMLDivElement>;
  weekOffDaysDropdownRef: React.RefObject<HTMLDivElement>;
  
  // Week-off days
  weekOffDays: string[];
  setWeekOffDays: (days: string[]) => void;
  
  // Calendar state
  showCalendar: boolean;
  activeFreelancerForCalendar: string;
  calendarSelectedDatesByFreelancer: { [key: string]: Date[] };
  
  // Table data
  tableRowsByFreelancer: any;
  serviceTableDataByFreelancer: any;
  deductionsByFreelancer: any;
  pensionDataByFreelancer: any;
  holidayDataByFreelancer: any;
  copiedInvoiceData: any;
  removedCopiedWorkingHours: Set<number>;
  
  // Options
  hoursPerDayOptions: Array<{ label: string; value: string }>;
  showDatesOptions: Array<{ label: string; value: string }>;
  serviceOptions: Array<{ label: string; value: string }>;
  showTooltipError: any;
  
  // Handlers
  handleInputChange: (field: string, value: any) => void;
  handleCalendarOpen: (freelancerId: string) => void;
  handleCalendarClose: () => void;
  handleDateSelect: (freelancerId: string, startDate: Date, endDate?: Date, selectedDates?: Date[]) => void;
  setIsHoursPerDayDropdownOpen: (value: boolean) => void;
  setIsShowDatesDropdownOpen: (value: boolean) => void;
  setIsWeekOffDaysDropdownOpen: (value: boolean) => void;
  handleRowChange: (freelancerId: string, rowId: string, field: string, value: any) => void;
  handleRemoveRow: (freelancerId: string, rowId: string) => void;
  handleAddWorkingHoursSection: (freelancerId: string, rowData?: any) => void;
  setServiceTableDataByFreelancer: React.Dispatch<React.SetStateAction<any>>;
  handleOpenDeductionModal: (freelancerId: string, index?: number) => void;
  setEditingDeductionData: (data: any) => void;
  setCurrentDeductionFreelancerId: (id: string) => void;
  setEditingDeductionIndex: (index: number | null) => void;
  setShowDeductionModal: (show: boolean) => void;
  setRemovedCopiedWorkingHours: React.Dispatch<React.SetStateAction<Set<number>>>;
  setDeductionsByFreelancer: React.Dispatch<React.SetStateAction<any>>;
  setPensionDataByFreelancer: React.Dispatch<React.SetStateAction<any>>;
  setHolidayDataByFreelancer: React.Dispatch<React.SetStateAction<any>>;
  
  // Additional props
  globalData: any;
  preferences: any;
  validateServiceDescriptionsRef: any;
  validateDatesRef?: any;
  t: (key: string) => string;
}

const DefaultInvoiceSection: React.FC<DefaultInvoiceSectionProps> = ({
  register,
  setValue,
  watch,
  errors,
  isRegularEngagement,
  currency,
  isHourlyPrice,
  isDailyPrice,
  isWeeklyPrice,
  isMonthlyPrice,
  deductionType,
  hoursPerDayType,
  workDates,
  showDates,
  selectedCustomer,
  isHoursPerDayDropdownOpen,
  isShowDatesDropdownOpen,
  isWeekOffDaysDropdownOpen,
  weekOffDays,
  setWeekOffDays,
  showCalendar,
  activeFreelancerForCalendar,
  calendarSelectedDatesByFreelancer,
  tableRowsByFreelancer,
  serviceTableDataByFreelancer,
  deductionsByFreelancer,
  pensionDataByFreelancer,
  holidayDataByFreelancer,
  copiedInvoiceData,
  removedCopiedWorkingHours,
  hoursPerDayOptions,
  showDatesOptions,
  serviceOptions,
  showTooltipError,
  handleInputChange,
  handleCalendarOpen,
  handleCalendarClose,
  handleDateSelect,
  setIsHoursPerDayDropdownOpen,
  setIsShowDatesDropdownOpen,
  setIsWeekOffDaysDropdownOpen,
  handleRowChange,
  handleRemoveRow,
  handleAddWorkingHoursSection,
  setServiceTableDataByFreelancer,
  handleOpenDeductionModal,
  setEditingDeductionData,
  setCurrentDeductionFreelancerId,
  setEditingDeductionIndex,
  setShowDeductionModal,
  setRemovedCopiedWorkingHours,
  setDeductionsByFreelancer,
  setPensionDataByFreelancer,
  setHolidayDataByFreelancer,
  globalData,
  preferences,
  validateServiceDescriptionsRef,
  validateDatesRef,
  t,
}) => {
  // Memoize the callback to prevent infinite re-renders in ServicesTable
  const handleServiceTableDataChange = useCallback((data: any) => {
    setServiceTableDataByFreelancer((prev: any) => ({
      ...prev,
      'default': data
    }));
  }, [setServiceTableDataByFreelancer]);

  return (
    <div className="user-section">
      <div className="user-content">
        {/* First Configuration Row - 5 fields matching Figma design */}
        {/* <Row className="config-row"> */}
          {/* Hours per day */}
          {/* <Col className="input-field" style={{ padding: '0 0px' }}>
            <Form.Label className="input-label">
              {t('HOURS_PER_DAY')}
              <Tooltip content={t('HOURS_PER_DAY_TOOLTIP')} position="top">
                <span className="info-icon">ⓘ</span>
              </Tooltip>
            </Form.Label>
            <div className={`input-box compact-input-box ${showTooltipError.nullHoursPerDayType ? 'error' : ''}`}>
              <Form.Control
                type="text"
                placeholder="08:00 or 08.00"
                className="input-value"
                value={hoursPerDayType || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || /^\d{0,2}([\.:])?(\d{0,2})?$/.test(value)) {
                    if (value.includes(':')) {
                      const [hours, minutes] = value.split(':');
                      const h = parseInt(hours || '0');
                      const m = parseInt(minutes || '0');
                      if (value === '' || ((hours === '' || h <= 23) && (minutes === '' || m <= 59))) {
                        setValue('hoursPerDayType', value);
                      }
                    } else {
                      const numValue = parseFloat(value);
                      if (value === '' || isNaN(numValue) || (numValue >= 0 && numValue <= 24)) {
                        setValue('hoursPerDayType', value);
                      }
                    }
                  }
                }}
                onBlur={(e) => {
                  const value = e.target.value;
                  if (!value) return;
                  
                  if (value.includes(':')) {
                    const [hours, minutes] = value.split(':');
                    const h = hours.padStart(2, '0');
                    const m = (minutes || '00').padStart(2, '0');
                    setValue('hoursPerDayType', `${h}:${m}`);
                  } else if (value.includes('.')) {
                    const parts = value.split('.');
                    const hours = parseInt(parts[0] || '0');
                    const decimal = parseInt(parts[1] || '0');
                    
                    if (decimal <= 59) {
                      setValue('hoursPerDayType', `${hours.toString().padStart(2, '0')}:${decimal.toString().padStart(2, '0')}`);
                    } else {
                      const totalMinutes = Math.round((parseFloat(value) - hours) * 60);
                      setValue('hoursPerDayType', `${hours.toString().padStart(2, '0')}:${totalMinutes.toString().padStart(2, '0')}`);
                    }
                  } else {
                    setValue('hoursPerDayType', value.padStart(2, '0') + ':00');
                  }
                }}
                onFocus={(e) => e.target.select()}
              />
            </div>
          </Col> */}

          {/* Hourly Price */}
          {/* <PriceInputField
            label={t('HOURLY_PRICE')}
            tooltipContent={t('HOURLY_PRICE_TOOLTIP')}
            fieldName="hourlyPrice"
            placeholder="500"
            currency={currency}
            register={register}
            errors={errors}
            hasError={showTooltipError.nullHourlyPrice}
            onChange={(value: any) => handleInputChange('hourlyPrice', value)}
          /> */}

          {/* Dates */}
          {/* <DatePickerField
            label={t('DATES')}
            tooltipContent={t('DATES_TOOLTIP')}
            fieldName="workDates"
            displayValue={workDates}
            placeholder={t('SELECT_DATES')}
            register={register}
            errors={errors}
            hasError={showTooltipError.nullWorkDates || showTooltipError.noDateSelected}
            showCalendar={showCalendar && activeFreelancerForCalendar === 'default'}
            onCalendarOpen={() => handleCalendarOpen('default')}
            onCalendarClose={handleCalendarClose}
            onDateSelect={(startDate: Date, endDate?: Date, selectedDates?: Date[]) => {
              if (startDate) {
                handleDateSelect('default', startDate, endDate, selectedDates || []);
              }
            }}
            initialSelectedDates={calendarSelectedDatesByFreelancer['default'] || []}
            allowRange={false}
            allowMultiple={true}
            required={isRegularEngagement}
            requiredMessage={t('PLEASE_SELECT_WORK_DATES')}
            t={t}
          /> */}

          {/* Show Dates */}
          {/* <Col>
            <CustomDropdown
              label={t('SHOW_DATES')}
              tooltipContent={t('SHOW_DATES_TOOLTIP')}
              selectedValue={showDates || ''}
              options={showDatesOptions}
              placeholder={t('SELECT_OPTION')}
              isOpen={isShowDatesDropdownOpen}
              onToggle={() => setIsShowDatesDropdownOpen(!isShowDatesDropdownOpen)}
              onSelect={(value: string) => {
                setValue('showDates', value);
                setIsShowDatesDropdownOpen(false);
              }}
              hasError={showTooltipError.nullShowDates}
            />
          </Col> */}
        {/* </Row> */}

        {/* Week-off days section - Only for Regular Engagement */}
        {/* {isRegularEngagement && (
          <Row className="config-row mt-3">
            <Col xs={12} style={{ padding: '0 0px' }}>
              <WeekOffDaysDropdown
                selectedDays={weekOffDays}
                isOpen={isWeekOffDaysDropdownOpen}
                onToggle={() => setIsWeekOffDaysDropdownOpen(!isWeekOffDaysDropdownOpen)}
                onChange={(newDays: string[]) => setWeekOffDays(newDays)}
                tooltipContent={t('SELECT_WEEK_OFF_DAYS') || 'Select days off from work'}
              />
            </Col>
          </Row>
        )} */}

        {/* Services Table - For non-freelancer (single invoice) */}
        <ServicesTable
          tableRows={tableRowsByFreelancer['default'] || []}
          serviceOptions={serviceOptions}
          currency={currency}
          isHourlyPrice={isHourlyPrice}
          isDailyPrice={isDailyPrice}
          isWeeklyPrice={isWeeklyPrice}
          isMonthlyPrice={isMonthlyPrice}
          deductionType={deductionType}
          t={t}
          onRowChange={(rowId: string, field: string, value: any) => handleRowChange('default', rowId, field, value)}
          onRemoveRow={(rowId: string) => handleRemoveRow('default', rowId)}
          onAddRow={(rowData: any) => handleAddWorkingHoursSection('default', rowData)}
          globalData={globalData}
          preferences={preferences}
          onServiceTableDataChange={handleServiceTableDataChange}
          validateServiceDescriptions={validateServiceDescriptionsRef}
          validateDates={validateDatesRef}
          freelancerId='default'
        />

        {/* Add Deductions Button */}
        <div style={{borderTop:"1px solid #E9EAEB", borderBottom:"1px solid #E9EAEB", width:"100%",}}>
        <div className="add-deductions-section" >
          <Button variant="primary" className="add-deductions-btn" onClick={() => handleOpenDeductionModal('default')}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 3.33333V12.6667M3.33333 8H12.6667" stroke="#252525" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {t('ADD_DEDUCTIONS')}
          </Button>
        </div>

        {/* Allowances & Deductions Table */}
        <AllowancesDeductionsTable
          copiedInvoiceData={copiedInvoiceData}
          deductionsByFreelancer={deductionsByFreelancer}
          removedCopiedWorkingHours={removedCopiedWorkingHours}
          freelancerId="default"
          t={t}
          onEditAllowance={(deductionData: any) => {
            setEditingDeductionData(deductionData);
            setCurrentDeductionFreelancerId('default');
            setEditingDeductionIndex(null);
            setShowDeductionModal(true);
          }}
          onRemoveAllowance={(originalIndex: number) => {
            setRemovedCopiedWorkingHours((prev: Set<number>) => new Set([...prev, originalIndex]));
          }}
          onEditDeduction={(index: number) => handleOpenDeductionModal('default', index)}
          onRemoveDeduction={(index: number) => {
            setDeductionsByFreelancer((prev: any) => ({
              ...prev,
              'default': (prev['default'] || []).filter((_: any, i: number) => i !== index)
            }));
          }}
        />
        </div>

        {/* Bottom Section with Checkboxes and Total */}
        <div className="bottom-section">
          <div className="checkboxes-section">
            <PensionHolidaySection
              freelancerId="default"
              freelancerName=""
              grossSalary={serviceTableDataByFreelancer['default']?.totalAmount || 0}
              isRegularEngagement={isRegularEngagement}
              globalData={globalData}
              t={t}
              pensionData={pensionDataByFreelancer}
              holidayData={holidayDataByFreelancer}
              onPensionChange={setPensionDataByFreelancer}
              onHolidayChange={setHolidayDataByFreelancer}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DefaultInvoiceSection;
